import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import PageHero from '../components/common/PageHero';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar, MapPin, Users, Clock, Award, DollarSign, CheckCircle } from 'lucide-react';
import axios from 'axios';

const Events = () => {
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming');
  const [registrations, setRegistrations] = useState(new Set());
  const [registering, setRegistering] = useState(null);

  useEffect(() => {
    fetchEvents();
    if (isAuthenticated) {
      fetchMyRegistrations();
    }
  }, [filter, isAuthenticated]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${backendUrl}/api/events`);
      
      // Filter events based on selected filter
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      let filteredEvents = response.data || [];
      
      if (filter === 'upcoming') {
        filteredEvents = filteredEvents.filter(event => {
          const eventDate = new Date(event.event_date);
          return eventDate >= today;
        });
      } else if (filter === 'past') {
        filteredEvents = filteredEvents.filter(event => {
          const eventDate = new Date(event.event_date);
          return eventDate < today;
        });
      }
      
      // Sort by date
      filteredEvents.sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
      
      setEvents(filteredEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast({
        title: 'Error',
        description: 'Failed to load events',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMyRegistrations = async () => {
    try {
      const response = await axios.get(
        `${backendUrl}/api/events/my-registrations`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      const regEventIds = new Set(response.data.map(reg => reg.event_id));
      setRegistrations(regEventIds);
    } catch (error) {
      console.error('Error fetching registrations:', error);
    }
  };

  const handleRegister = async (eventId) => {
    if (!isAuthenticated) {
      toast({
        title: 'Login Required',
        description: 'Please login to register for events',
        variant: 'destructive'
      });
      navigate('/login');
      return;
    }

    const event = events.find(e => e.id === eventId);
    const hasFee = event && event.registration_fee > 0;

    try {
      setRegistering(eventId);

      if (hasFee) {
        // Initiate PayFast payment for paid events
        const response = await axios.post(
          `${backendUrl}/api/payments/event/initiate`,
          { event_id: eventId },
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        );

        const { payfast_url, payment_data } = response.data;

        // Create and submit PayFast form
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = payfast_url;

        Object.entries(payment_data).forEach(([key, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = String(value);
          form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
        return;
      }

      // Free event - register directly
      await axios.post(
        `${backendUrl}/api/events/${eventId}/register`,
        { event_id: eventId },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      toast({
        title: 'Success!',
        description: 'You have been registered! View your QR code in "My Events"',
        duration: 5000,
      });
      
      // Update registrations
      setRegistrations(new Set([...registrations, eventId]));
      
    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle Pydantic validation errors
      let errorMsg = 'Registration failed';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Pydantic validation error format
          errorMsg = error.response.data.detail.map(err => err.msg).join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMsg = error.response.data.detail;
        }
      }
      
      toast({
        title: 'Registration Failed',
        description: errorMsg,
        variant: 'destructive'
      });
    } finally {
      setRegistering(null);
    }
  };

  const getEventStatus = (event) => {
    const eventDate = new Date(event.event_date);
    const today = new Date();
    
    if (eventDate < today) {
      return { label: 'Past Event', color: 'bg-gray-100 text-gray-800' };
    }
    
    if (event.status === 'cancelled') {
      return { label: 'Cancelled', color: 'bg-red-100 text-red-800' };
    }
    
    if (event.registered_count >= event.capacity) {
      return { label: 'Full', color: 'bg-orange-100 text-orange-800' };
    }
    
    return { label: 'Open', color: 'bg-green-100 text-green-800' };
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <PageHero title="Events" />
        <div className="container mx-auto px-4 py-12 flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2c3e7d]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHero title="Events" />
      
      <div className="container mx-auto px-4 py-8 md:py-12">
        {/* Filter Tabs */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setFilter('all')}
            className={`px-6 py-2 rounded-full font-medium transition-colors ${
              filter === 'all'
                ? 'bg-[#2c3e7d] text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            All Events
          </button>
          <button
            onClick={() => setFilter('upcoming')}
            className={`px-6 py-2 rounded-full font-medium transition-colors ${
              filter === 'upcoming'
                ? 'bg-[#2c3e7d] text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            Upcoming
          </button>
          <button
            onClick={() => setFilter('past')}
            className={`px-6 py-2 rounded-full font-medium transition-colors ${
              filter === 'past'
                ? 'bg-[#2c3e7d] text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            Past Events
          </button>
        </div>

        {/* Events Grid */}
        {events.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Calendar className="mx-auto h-16 w-16 text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Events Found</h3>
              <p className="text-gray-600">
                {filter === 'upcoming' ? 'No upcoming events at the moment.' : 'No events found for this filter.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {events.map((event) => {
              const status = getEventStatus(event);
              const isRegistered = registrations.has(event.id);
              const isFull = event.registered_count >= event.capacity;
              const isPast = new Date(event.event_date) < new Date();
              
              return (
                <Card key={event.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg pr-2">{event.title}</CardTitle>
                      <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${status.color}`}>
                        {status.label}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div 
                      className="text-sm text-gray-600 line-clamp-2"
                      dangerouslySetInnerHTML={{
                        __html: event.description?.replace(
                          /(https?:\/\/[^\s]+)/g,
                          '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">$1</a>'
                        ) || ''
                      }}
                    />
                    
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="mr-2 h-4 w-4 flex-shrink-0" />
                      <span>{new Date(event.event_date).toLocaleDateString('en-ZA', { 
                        weekday: 'short', 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}</span>
                    </div>
                    
                    {event.event_time && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Clock className="mr-2 h-4 w-4 flex-shrink-0" />
                        <span>{event.event_time}</span>
                      </div>
                    )}
                    
                    {event.location && (
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="mr-2 h-4 w-4 flex-shrink-0" />
                        <span className="line-clamp-1">{event.location}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center text-sm text-gray-600">
                      <Users className="mr-2 h-4 w-4 flex-shrink-0" />
                      <span>{event.registered_count || 0} / {event.capacity} registered</span>
                    </div>
                    
                    {event.cpd_points && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Award className="mr-2 h-4 w-4 flex-shrink-0" />
                        <span>{event.cpd_points} CPD Points</span>
                      </div>
                    )}
                    
                    {event.registration_fee > 0 && (
                      <div className="flex items-center text-sm text-gray-600">
                        <DollarSign className="mr-2 h-4 w-4 flex-shrink-0" />
                        <span>R {event.registration_fee.toFixed(2)}</span>
                      </div>
                    )}
                    
                    <div className="pt-4 border-t space-y-2">
                      {isRegistered ? (
                        <>
                          <Button 
                            onClick={() => navigate('/my-events')}
                            className="w-full bg-green-600 hover:bg-green-700"
                            size="sm"
                          >
                            <CheckCircle className="mr-2 h-4 w-4" />
                            View QR Code
                          </Button>
                          <div className="text-xs text-center text-gray-500">
                            Go to My Events to check in
                          </div>
                        </>
                      ) : (
                        <Button 
                          onClick={() => handleRegister(event.id)}
                          disabled={registering === event.id || isFull || isPast}
                          className="w-full bg-[#2c3e7d] hover:bg-[#1e2a5a]"
                          size="sm"
                        >
                          {registering === event.id ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              {event.registration_fee > 0 ? 'Processing...' : 'Registering...'}
                            </>
                          ) : isFull ? (
                            'Event Full'
                          ) : isPast ? (
                            'Event Ended'
                          ) : event.registration_fee > 0 ? (
                            `Register & Pay R ${event.registration_fee.toFixed(2)}`
                          ) : (
                            'Register Now'
                          )}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Events;
